import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../../hooks';
import {
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Collapse,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import axiosInstance from '../../../services/axiosInstance';
import BookingCard from './BookingCard'; // Ajusta la ruta según la ubicación

// Componente principal
export const Bookings = () => {
  const { user } = useContext(UserContext);
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    status: '',
    courtId: '',
  });
  const [courts, setCourts] = useState([]);
  const navigate = useNavigate();

  const getBookingEndDate = booking => {
    if (!booking?.dateBooked) return null;
    const baseDate = new Date(booking.dateBooked);
    const timeRange = String(booking.timeBooked || '').trim();
    if (!timeRange) return baseDate;

    const parts = timeRange.split('-').map(part => part.trim());
    const timePart = (parts[1] || parts[0] || '').trim();
    const [hours, minutes] = timePart.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return baseDate;

    const endDate = new Date(baseDate);
    endDate.setHours(hours, minutes, 0, 0);
    return endDate;
  };

  const isExpiredPendingBooking = booking => {
    if (!booking || booking.deletedAt) return false;
    if (booking.paymentStatus !== 'C') return false;
    const endDate = getBookingEndDate(booking);
    if (!endDate) return false;
    return endDate.getTime() < Date.now();
  };

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const response = await axiosInstance.get(`/users/bookings`);
        if (response.success) {
          const normalizedBookings = response.bookings.map(booking => ({
            ...booking,
            isExpiredPending: isExpiredPendingBooking(booking),
          }));
          const visibleBookings = normalizedBookings.filter(
            booking => !booking.deletedAt
          );
          setBookings(visibleBookings);
          setFilteredBookings(visibleBookings);
        } else {
          toast.error('Error al mostrar las reservas.');
        }
      } catch (error) {
        console.error('Error fetching bookings:', error);
        toast.error('Error al cargar las reservas.');
      } finally {
        setLoading(false);
      }
    };

    const fetchCourts = async () => {
      try {
        const response = await axiosInstance.get('/courts');
        if (response.success) {
          setCourts(response.courts);
        } else {
          console.error('No se pudieron cargar las pistas.');
        }
      } catch (error) {
        console.error('Error al cargar las pistas:', error);
      }
    };

    fetchBookings();
    fetchCourts();
  }, [user.id]);

  const handleFilterChange = e => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    let filtered = bookings;

    if (filters.fromDate || filters.toDate) {
      filtered = filtered.filter(booking => {
        const bookingDate = new Date(
          booking.yearBooked,
          booking.monthBooked,
          booking.dayBooked
        );
        const fromDate = filters.fromDate ? new Date(filters.fromDate) : null;
        const toDate = filters.toDate ? new Date(filters.toDate) : null;

        return (
          (!fromDate || bookingDate >= fromDate) &&
          (!toDate || bookingDate <= toDate)
        );
      });
    }

    if (filters.status) {
      filtered = filtered.filter(
        booking =>
          (filters.status === 'P' &&
            booking.paymentStatus === 'P' &&
            !booking.deletedAt) ||
          (filters.status === 'Pendiente' &&
            booking.paymentStatus !== 'P' &&
            !booking.deletedAt &&
            !booking.isExpiredPending) ||
          (filters.status === 'Cancelada' &&
            (!!booking.deletedAt || booking.isExpiredPending))
      );
    }

    if (filters.courtId) {
      filtered = filtered.filter(booking => {
        const bookingCourtId =
          typeof booking.courtId === 'object'
            ? booking.courtId?._id
            : booking.courtId;
        return bookingCourtId === filters.courtId;
      });
    }

    setFilteredBookings(filtered);
  };

  const clearFilters = () => {
    setFilters({ fromDate: '', toDate: '', status: '', courtId: '' });
    setFilteredBookings(bookings);
  };

  const handleViewDetails = bookingId => {
    navigate(`/bookings/${bookingId}`);
  };

  return (
    <Box>
      <Typography variant="h5" align="center" gutterBottom>
        Mis Reservas
      </Typography>

      {/* Filtros */}
      <Box display="flex" justifyContent="right" my={2}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? 'Ocultar Filtros' : 'Filtrar'}
        </Button>
      </Box>

      <Collapse in={showFilters}>
        <Box
          mb={3}
          display="flex"
          flexWrap="wrap"
          gap={2}
          justifyContent="center"
          flexDirection={{ xs: 'column', sm: 'row' }}
        >
          <TextField
            label="Desde"
            type="date"
            name="fromDate"
            value={filters.fromDate}
            onChange={handleFilterChange}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Hasta"
            type="date"
            name="toDate"
            value={filters.toDate}
            onChange={handleFilterChange}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select
            label="Estado"
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            sx={{
              minWidth: { xs: '100%', sm: 100, md: 150 }, // Ajuste dinámico según el tamaño de la pantalla
              maxWidth: 300, // Opcional: establece un ancho máximo para evitar que crezca demasiado
            }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="P">Pagado</MenuItem>
            <MenuItem value="Pendiente">Pendiente</MenuItem>
            <MenuItem value="Cancelada">Cancelada</MenuItem>
          </TextField>
          <TextField
            select
            label="Tipo de Pista"
            name="courtId"
            value={filters.courtId}
            onChange={handleFilterChange}
            sx={{
              minWidth: { xs: '100%', sm: 150 },
              maxWidth: 300,
            }}
          >
            <MenuItem value="">Todas</MenuItem>
            {courts.map(court => (
              <MenuItem key={court._id} value={court._id}>
                {`${court.type?.toUpperCase()} - ${court.name}`}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" color="primary" onClick={applyFilters}>
            Aplicar Filtros
          </Button>
          <Button variant="outlined" color="secondary" onClick={clearFilters}>
            Limpiar Filtros
          </Button>
        </Box>
      </Collapse>

      {/* Reservas */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : filteredBookings.length > 0 ? (
        <Grid container spacing={2}>
          {filteredBookings.map(booking => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={booking._id}>
              <BookingCard
                booking={booking}
                onViewDetails={handleViewDetails}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Typography align="center" color="textSecondary" mt={4}>
          No hay reservas que coincidan con los filtros.
        </Typography>
      )}
    </Box>
  );
};

export default Bookings;
